import React, { useState, useEffect, useContext } from 'react'
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClipboardList, faSave, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons'
import Swal from 'sweetalert2'
import { useParams } from 'react-router-dom'
import CustomLoader from '../../layouts/CustomLoader'
import AuthContext from '../../../middlewares/AuthContext'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface PlanData {
  id             : number
  name           : string
  description    : string
  price          : number
  price_mxn      : number
  best_value     : boolean
  visibility     : boolean
  order          : number
  expiry_time    : number
  quantity       : number
  stripe_id      : string
  list_description: string[]
  Time           : { name: string }
  Product        : { name: string }
}

interface EditForm {
  name            : string
  description     : string
  best_value      : number
  visibility      : number
  order           : number
  list_description: string[]
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const EditPlan: React.FC = () => {
  const { getHeaders } = useContext(AuthContext) as AuthCtx
  const { t }          = useTranslation()
  const { id }         = useParams<{ id: string }>()

  const [plan, setPlan]                   = useState<PlanData | null>(null)
  const [initialValues, setInitialValues] = useState<EditForm | null>(null)
  const [loaded, setLoaded]               = useState(false)
  const [loading, setLoading]             = useState(false)

  const formSchema = Yup.object().shape({
    name            : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    description     : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    best_value      : Yup.number().min(1).max(2).required(t('utilities.is_a_required_field')),
    visibility      : Yup.number().min(1).max(2).required(t('utilities.is_a_required_field')),
    order           : Yup.number().min(1).max(99).required(t('utilities.is_a_required_field')),
    list_description: Yup.array()
      .of(Yup.string().required(t('utilities.is_a_required_field')).min(3))
      .required(t('products.plans.at_least'))
      .min(1, t('products.plans.at_least')),
  })

  useEffect(() => {
    const fetch = async () => {
      try {
        const headers = await getHeaders()
        const res     = await axios.get(`${url}plans/edit/${id}`, { headers })
        const p: PlanData = res.data.message
        setPlan(p)
        setInitialValues({
          name            : p.name,
          description     : p.description,
          best_value      : p.best_value ? 2 : 1,
          visibility      : p.visibility ? 2 : 1,
          order           : p.order,
          list_description: p.list_description,
        })
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
      } finally {
        setLoaded(true)
      }
    }
    fetch()
  }, [])

  const handleSubmit = async (values: EditForm) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.put(`${url}plans/update/${id}`, values, { headers })
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  if (!loaded) return <CustomLoader />

  if (!plan || !initialValues) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8', fontWeight: 700 }}>
        {t('products.plans.not_exist_plan')}
      </div>
    )
  }

  return (
    <div className="row justify-content-center">
      <div className="col-lg-11">

        {/* Header */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden', marginBottom: '1.5rem' }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: `${BRAND}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FontAwesomeIcon icon={faClipboardList} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {plan.Product.name}
              </p>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                {t('products.plans.edition')}
              </p>
            </div>
          </div>
        </div>

        <div className="row g-4">

          {/* Info sidebar */}
          <div className="col-md-3">
            <div style={{ backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
              <div style={{ height: 3, backgroundColor: '#3b82f6' }} />
              <div style={{ padding: '1.25rem' }}>
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Plan info
                </p>
                {[
                  { label: 'Product', value: plan.Product.name },
                  { label: 'Price USD', value: `$${parseFloat(String(plan.price)).toFixed(2)}` },
                  { label: 'Price MXN', value: `$${parseFloat(String(plan.price_mxn)).toFixed(2)}` },
                  { label: 'Expiry', value: `${plan.expiry_time} ${plan.Time.name}(s)` },
                  { label: 'Quantity', value: String(plan.quantity) },
                  { label: 'Stripe ID', value: plan.stripe_id || '—' },
                ].map((item, i) => (
                  <div key={i} style={{ marginBottom: '0.5rem' }}>
                    <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>{item.label}</p>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#1e293b', fontWeight: 600, wordBreak: 'break-all' }}>{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="col-md-9">
            <div style={{ backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
              <div style={{ height: 3, backgroundColor: BRAND }} />
              <div style={{ padding: '1.5rem' }}>
                <Formik initialValues={initialValues} validationSchema={formSchema} onSubmit={handleSubmit} enableReinitialize>
                  {({ values }) => (
                    <Form>

                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <FieldLabel>{t('products.plans.name')} *</FieldLabel>
                          <Field type="text" className="form-control form-control-sm" name="name" />
                          <ErrorMessage name="name" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                        <div className="col-md-6">
                          <FieldLabel>{t('products.plans.description')} *</FieldLabel>
                          <Field type="text" className="form-control form-control-sm" name="description" />
                          <ErrorMessage name="description" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                      </div>

                      <div className="row g-3 mb-3">
                        <div className="col-md-4">
                          <FieldLabel>{t('products.plans.select_a_plan')} *</FieldLabel>
                          <Field as="select" className="form-select form-select-sm" name="best_value">
                            <option value={1}>{t('products.plans.normal')}</option>
                            <option value={2}>{t('products.plans.best')}</option>
                          </Field>
                          <ErrorMessage name="best_value" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                        <div className="col-md-4">
                          <FieldLabel>{t('products.plans.select_a_option')} *</FieldLabel>
                          <Field as="select" className="form-select form-select-sm" name="visibility">
                            <option value={1}>{t('products.hidden')}</option>
                            <option value={2}>{t('products.show')}</option>
                          </Field>
                          <ErrorMessage name="visibility" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                        <div className="col-md-4">
                          <FieldLabel>{t('products.order')} *</FieldLabel>
                          <Field type="number" className="form-control form-control-sm" name="order" />
                          <ErrorMessage name="order" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                      </div>

                      {/* FieldArray */}
                      <div className="mb-4">
                        <FieldArray name="list_description" render={({ remove, push }) => (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                              <FieldLabel>{t('products.plans.description_list')}</FieldLabel>
                              <button
                                type="button"
                                onClick={() => push('')}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.2rem 0.6rem', borderRadius: 9999, backgroundColor: BRAND, color: '#fff', fontWeight: 700, fontSize: '0.75rem', border: 'none', cursor: 'pointer' }}
                              >
                                <FontAwesomeIcon icon={faPlus} /><span>Add</span>
                              </button>
                            </div>
                            {values.list_description.map((_, index) => (
                              <div key={index} className="mb-2">
                                <div className="input-group input-group-sm">
                                  <Field type="text" className="form-control" name={`list_description.${index}`} placeholder={`#${index + 1}.- Description`} />
                                  <button className="btn btn-danger btn-sm" type="button" onClick={() => remove(index)}>
                                    <FontAwesomeIcon icon={faMinus} />
                                  </button>
                                </div>
                                <ErrorMessage name={`list_description.${index}`} component="p" className="text-danger small fw-bold mt-1 mb-0" />
                              </div>
                            ))}
                            {values.list_description.length === 0 && (
                              <p className="text-danger small fw-bold mb-0">{t('products.plans.at_least')}</p>
                            )}
                          </>
                        )} />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', padding: '0.55rem 1.5rem', borderRadius: 9999, backgroundColor: loading ? '#94a3b8' : BRAND, color: '#fff', fontWeight: 700, fontSize: '0.875rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', boxShadow: loading ? 'none' : `0 4px 14px ${BRAND}55`, transition: 'filter 0.15s' }}
                        onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = 'brightness(1.09)' }}
                        onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)' }}
                      >
                        {loading
                          ? <span className="spinner-border spinner-border-sm" role="status" />
                          : <FontAwesomeIcon icon={faSave} />
                        }
                        <span>Update Plan</span>
                      </button>

                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

export default EditPlan
